import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection
} from "@angular/core";
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  RouteReuseStrategy
} from "@angular/router";
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS
} from "@angular/common/http";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MatPaginatorIntl } from "@angular/material/paginator";
import {
  MAT_DATE_LOCALE,
  provideNativeDateAdapter
} from "@angular/material/core";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatListModule } from "@angular/material/list";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatRadioModule } from "@angular/material/radio";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatDialogModule } from "@angular/material/dialog";
import { PortalModule } from "@angular/cdk/portal";
import { ClipboardModule } from "@angular/cdk/clipboard";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { LeafletModule } from "@bluehalo/ngx-leaflet";
import { PlotlyViaWindowModule } from "angular-plotly.js";
import { AngularSvgIconModule } from "angular-svg-icon";
import "leaflet-textpath";

import { routes } from "./app.routes";
import { AuthInterceptor } from "./services/auth-interceptor.service";
import { CustomMatPaginatorIntl } from "./services/custom-mat-paginator-intl";
import { XlsxExporter } from "./services/xlsx-exporter";
import { GeoUtils } from "./services/geo-utils";
import { ContainerTypeControllerService } from "./services/container-type-controller.service";
import { FiltersControllerService } from "./services/filters.service";
import { AnalyticsControllerService } from "./services/analytics-controller.service";
import { SiteControllerService } from "./services/site-controller.service";
import { ContainerTypeAuthSiteControllerService } from "./services/container-type-auth-site-controller.service";
import { InternalUserControllerService } from "./services/internal-user-controller.service";
import { ExternalUserControllerService } from "./services/external-user-controller.service";
import { MacAddressControllerService } from "./services/mac-address-controller.service";
import { PolyvalentControllerService } from "./services/polyvalent-controller.service";
import { SitePostalControllerService } from "./services/site-postal-controller.service";
import { DeviceContainerControllerService } from "./services/device-container-controller.service";
import { DeviceContainerStateControllerService } from "./services/device-container-state-controller.service";
import { DeviceOffContractControllerService } from "./services/device-off-contract-controller.service";
import { UserControllerService } from "./services/user-controller.service";
import { HttpClientWrapper } from "./services/http-client.wrapper";
import { IncidentHistoricalControllerService } from "./services/incident-historical-controller.service";
import { ProfileContainerTypeControllerService } from "./services/profile-container-type-controller.service";
import { RightContainerTypeControllerService } from "./services/right-container-types-controller.service";
import { RightControllerService } from "./services/right-controller.service";
import { ReferenceNameControllerService } from "./services/reference-name-controller.service";
import { DataDecompressorService } from "./services/data-decompressor.service";
import { WebSocketService } from "./services/web-socket.service";
import { IncidentControllerService } from "./services/incident-controller.service";
import { ColorService } from "./services/color.service";
import { ProvisioningControllerService } from "./services/provisionning-controller.service";
import { SlashToLineBreak } from "./pipes/slash-line-break.pipe";
import { CustomRight } from "./pipes/custom-right-pipe";
import { DateNullable } from "./pipes/date-pipe";
import { TabDetailRouteReuseStrategy } from "./services/tab-detail-route-reuse.strategy";

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: "enabled" })
    ),
    importProvidersFrom(
      PortalModule,
      BrowserModule,
      BrowserAnimationsModule,
      MatButtonToggleModule,
      MatListModule,
      MatTooltipModule,
      MatExpansionModule,
      MatRadioModule,
      MatProgressSpinnerModule,
      MatAutocompleteModule,
      MatDatepickerModule,
      FormsModule,
      MatSnackBarModule,
      ReactiveFormsModule,
      HttpClientModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }),
      LeafletModule,
      CommonModule,
      PlotlyViaWindowModule,
      MatSlideToggleModule,
      AngularSvgIconModule.forRoot(),
      ClipboardModule,
      MatDialogModule
    ),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: RouteReuseStrategy, useClass: TabDetailRouteReuseStrategy },
    {
      provide: MatPaginatorIntl,
      useClass: CustomMatPaginatorIntl
    },
    // MatDatepickerModule only provides MatDatepickerIntl, so the datepicker
    // needs a DateAdapter + MAT_DATE_FORMATS of its own: without them every
    // <app-date-picker> throws in its constructor. Same providers the deleted
    // MatNativeDateModule used to bring in through app.module.
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: "fr-FR" },
    XlsxExporter,
    GeoUtils,
    DatePipe,
    ContainerTypeControllerService,
    FiltersControllerService,
    AnalyticsControllerService,
    SiteControllerService,
    ContainerTypeAuthSiteControllerService,
    InternalUserControllerService,
    ExternalUserControllerService,
    MacAddressControllerService,
    PolyvalentControllerService,
    SitePostalControllerService,
    DeviceContainerControllerService,
    DeviceContainerStateControllerService,
    DeviceOffContractControllerService,
    UserControllerService,
    HttpClientWrapper,
    IncidentHistoricalControllerService,
    ProfileContainerTypeControllerService,
    RightContainerTypeControllerService,
    RightControllerService,
    ReferenceNameControllerService,
    DataDecompressorService,
    WebSocketService,
    IncidentControllerService,
    ColorService,
    ProvisioningControllerService,
    SlashToLineBreak,
    CustomRight,
    DateNullable
  ]
};
